
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/hooks/useAuth";
import { ShoppingCart } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface CheckoutFormProps {
  customerName: string;
  setCustomerName: (name: string) => void;
  customerPhone: string;
  setCustomerPhone: (phone: string) => void;
  deliveryAddress: string;
  setDeliveryAddress: (address: string) => void;
  paymentMethod: string;
  setPaymentMethod: (method: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  needChange: boolean;
  setNeedChange: (needChange: boolean) => void;
  changeAmount: string;
  setChangeAmount: (amount: string) => void;
  sodaFlavor?: string;
  setSodaFlavor?: (flavor: string) => void;
  isFirstPurchase?: boolean;
  cardType?: string;
  setCardType?: (type: string) => void;
  selectedExtras?: string[];
  setSelectedExtras?: (extras: string[]) => void;
}

const CheckoutForm = ({
  customerName,
  setCustomerName,
  customerPhone,
  setCustomerPhone,
  deliveryAddress,
  setDeliveryAddress,
  paymentMethod,
  setPaymentMethod,
  onSubmit,
  loading,
  needChange,
  setNeedChange,
  changeAmount,
  setChangeAmount,
  sodaFlavor,
  setSodaFlavor,
  isFirstPurchase = false,
  cardType,
  setCardType,
  selectedExtras = [],
  setSelectedExtras
}: CheckoutFormProps) => {
  const { user } = useAuth();
  
  // Soda flavor options
  const sodaOptions = [
    "Coca-Cola",
    "Guaraná",
    "Sprite",
    "Fanta Laranja",
    "Fanta Uva",
    "Pepsi",
    "Outro (especificar nos detalhes do pedido)"
  ];

  // Card type options
  const cardTypeOptions = [
    "Crédito",
    "Débito"
  ];

  // Extras options
  const extrasOptions = [
    { id: "catupiry", name: "Catupiry", price: 3.00 },
    { id: "cheddar", name: "Cheddar", price: 2.50 },
    { id: "4queijos", name: "4 Queijos", price: 4.00 },
    { id: "cebola", name: "Cebola Grelhada", price: 1.50 },
    { id: "picles", name: "Picles", price: 1.00 }
  ];

  const handleExtraChange = (extraId: string, checked: boolean) => {
    if (!setSelectedExtras) return;
    
    if (checked) {
      setSelectedExtras([...selectedExtras, extraId]);
    } else {
      setSelectedExtras(selectedExtras.filter(id => id !== extraId));
    }
  };

  const getExtrasTotal = () => {
    return selectedExtras.reduce((total, extraId) => {
      const extra = extrasOptions.find(e => e.id === extraId);
      return total + (extra?.price || 0);
    }, 0);
  };

  return (
    <div className="bg-card text-card-foreground rounded-lg p-6 border border-border shadow-sm">
      <h2 className="text-xl font-semibold text-foreground mb-6">Detalhes da Entrega</h2>
      
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-foreground">Nome Completo</Label>
          <Input
            id="name"
            placeholder="Seu nome completo"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            required
            className="bg-background border-input text-foreground"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-foreground">Telefone</Label>
          <Input
            id="phone"
            placeholder="Ex: (51) 99999-9999"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            required
            className="bg-background border-input text-foreground"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="address" className="text-foreground">Endereço de Entrega</Label>
          <Textarea
            id="address"
            placeholder="Rua, número, bairro, complemento"
            value={deliveryAddress}
            onChange={(e) => setDeliveryAddress(e.target.value)}
            required
            className="bg-background border-input text-foreground"
          />
        </div>

        {/* Adicionais Section */}
        {setSelectedExtras && (
          <div className="space-y-3">
            <Label className="text-base font-medium text-foreground">Adicionais</Label>
            <p className="text-sm text-muted-foreground">Selecione os adicionais que deseja incluir no seu pedido:</p>
            <div className="space-y-3 bg-muted/40 p-4 rounded-lg border border-border">
              {extrasOptions.map((extra) => (
                <div key={extra.id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id={extra.id}
                      checked={selectedExtras.includes(extra.id)}
                      onCheckedChange={(checked) => handleExtraChange(extra.id, checked as boolean)}
                      className="border-border data-[state=checked]:bg-krecao-red data-[state=checked]:border-krecao-red"
                    />
                    <Label htmlFor={extra.id} className="cursor-pointer text-foreground">
                      {extra.name}
                    </Label>
                  </div>
                  <span className="text-krecao-yellow font-medium">
                    +R$ {extra.price.toFixed(2)}
                  </span>
                </div>
              ))}
              {selectedExtras.length > 0 && (
                <div className="border-t border-border pt-3 mt-3">
                  <div className="flex justify-between items-center font-medium">
                    <span className="text-foreground">Total dos Adicionais:</span>
                    <span className="text-krecao-yellow">R$ {getExtrasTotal().toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Mini soda flavor selection for registered users on first purchase */}
        {user && isFirstPurchase && setSodaFlavor && (
          <div className="space-y-2">
            <Label htmlFor="soda-flavor" className="text-foreground">Sabor do Mini Refrigerante (Grátis na primeira compra)</Label>
            <Select value={sodaFlavor} onValueChange={setSodaFlavor}>
              <SelectTrigger className="bg-background border-input text-foreground">
                <SelectValue placeholder="Escolha o sabor do refrigerante" />
              </SelectTrigger>
              <SelectContent>
                {sodaOptions.map((flavor) => (
                  <SelectItem key={flavor} value={flavor}>
                    {flavor}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Benefício exclusivo para clientes cadastrados na primeira compra!</p>
          </div>
        )}
        
        <div className="space-y-3">
          <Label className="text-foreground">Forma de Pagamento</Label>
          <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="flex flex-col space-y-2">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="dinheiro" id="dinheiro" />
              <Label htmlFor="dinheiro" className={`cursor-pointer ${paymentMethod === 'dinheiro' ? 'text-krecao-yellow font-bold' : 'text-foreground'}`}>
                Dinheiro
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="cartao" id="cartao" />
              <Label htmlFor="cartao" className={`cursor-pointer ${paymentMethod === 'cartao' ? 'text-krecao-yellow font-bold' : 'text-foreground'}`}>
                Cartão (na entrega)
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="pix" id="pix" />
              <Label htmlFor="pix" className={`cursor-pointer ${paymentMethod === 'pix' ? 'text-krecao-yellow font-bold' : 'text-foreground'}`}>
                PIX (enviaremos os dados)
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* Card type selection */}
        {paymentMethod === 'cartao' && setCardType && (
          <div className="space-y-2">
            <Label htmlFor="card-type" className="text-foreground">Tipo de Cartão</Label>
            <Select value={cardType} onValueChange={setCardType}>
              <SelectTrigger className="bg-background border-input text-foreground">
                <SelectValue placeholder="Selecione o tipo de cartão" />
              </SelectTrigger>
              <SelectContent>
                {cardTypeOptions.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {/* Change section */}
        {paymentMethod === 'dinheiro' && (
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <input 
                type="checkbox" 
                id="need-change"
                checked={needChange}
                onChange={(e) => setNeedChange(e.target.checked)}
                className="rounded border-input"
              />
              <Label htmlFor="need-change" className="cursor-pointer text-foreground">Preciso de troco</Label>
            </div>
            
            {needChange && (
              <div className="space-y-2">
                <Label htmlFor="change-amount" className="text-foreground">Troco para quanto?</Label>
                <Input
                  id="change-amount"
                  type="text"
                  placeholder="Ex: 50,00"
                  value={changeAmount}
                  onChange={(e) => setChangeAmount(e.target.value)}
                  className="bg-background border-input text-foreground"
                />
              </div>
            )}
          </div>
        )}
        
        <Button type="submit" disabled={loading} className="w-full bg-krecao-yellow hover:bg-krecao-yellow/90 text-black font-semibold mt-4 shadow-sm">
          <ShoppingCart className="mr-2 h-4 w-4" />
          {loading ? "Processando..." : "Finalizar Pedido"}
        </Button>
        
        <p className="text-sm text-muted-foreground text-center mt-2">
          Seu pedido será enviado para o WhatsApp do K-recão Lanches
        </p>
      </form>
    </div>
  );
};

export default CheckoutForm;
